import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Colour roles are fixed (docs/01-design-system.md §2.1):
 *   primary   — green, every committing action: save, submit, approve, add
 *   danger    — red, every destroying action: reject, delete, remove
 *   secondary — neutral outline, the default
 *   ghost/quiet — chrome-free, for dense rails and toolbars
 *
 * Brand red is never a button. It is the topbar rule and the active-nav
 * indicator, both non-interactive. If a red button ever ships for brand
 * reasons the affirmative/destructive split breaks.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'quiet' | 'danger'
export type ButtonSize = 'sm' | 'md'

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-affirm text-white hover:bg-affirm-800 border border-transparent',
  secondary: 'bg-surface text-ink border border-rule-strong hover:bg-surface-sunk',
  ghost: 'bg-transparent text-ink-muted border border-transparent hover:bg-surface-sunk',
  quiet: 'bg-transparent text-brand-blue border border-transparent hover:bg-brand-blue-50',
  danger: 'bg-brand-red text-white hover:bg-brand-red-700 border border-transparent',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-[12px] gap-1.5',
  md: 'h-8 px-3 text-[13px] gap-1.5',
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-[6px] font-head font-semibold',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading && <Loader2 size={13} className="animate-spin" aria-hidden />}
      {children}
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Required — an icon-only control with no accessible name is unusable. */
  label: string
  children: ReactNode
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] text-ink-muted',
        'transition-colors hover:bg-surface-sunk hover:text-ink',
        'disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
})
